import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Checkbox,
  Collapse,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Menu,
  MenuItem,
} from "@mui/material";
import { ExpandMore, ExpandLess, Add, Delete, Error as ErrorIcon, ContentCopy } from "@mui/icons-material";
import DatePicker from "react-datepicker";
import moment from "moment";
import { showAlert } from "../../Services/Shared.service";
import { createPackagesBulk } from "../../Services/Packages.service";
import { useRateConstraints, validatePerKmCharge } from "../../hooks/useRateConstraints";

/**
 * PackageRateCreateForm - Shared component for creating package rates
 * with BulkEditForm-style sidebar UI and validations.
 */
const PackageRateCreateForm = ({ packages = [], onComplete, onBack, initialRates = null, onRatesChange = null, onSubmit = null }) => {
  const [selectedPackageIndex, setSelectedPackageIndex] = useState(0);
  const [packageRates, setPackageRates] = useState(() => {
    if (initialRates) return initialRates;
    const initial = {};
    packages.forEach((_, idx) => {
      initial[idx] = [];
    });
    return initial;
  });

  // Effect to propagate changes to parent
  useEffect(() => {
    if (onRatesChange) {
      onRatesChange(packageRates);
    }
  }, [packageRates, onRatesChange]);

  const [expandedRows, setExpandedRows] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  // Validation errors: { [pkgIndex]: { [rateIndex]: { fieldName: errorMessage } } }
  const [validationErrors, setValidationErrors] = useState({});
  // State for copy rates menu
  const [copyMenuAnchor, setCopyMenuAnchor] = useState(null);
  // Track if user has attempted to save (only show sidebar error icons after save attempt)
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const rewardConstraints = useRateConstraints();


  function getEmptyRate() {
    return {
      minPkgDistance: "",
      maxPkgDistance: "",
      pkgCharge: "",
      addnlPerKmCharge: "",
      isTimeBased: false,
      timeFrom: null,
      timeTo: null,
    };
  }

  // Validate a single rate and return errors object (same as BulkEditForm)
  const validateRate = (rate) => {
    const errors = {};
    
    if (!rate.minPkgDistance && rate.minPkgDistance !== 0) {
      errors.minPkgDistance = 'Minimum Distance is required';
    } else if (Number(rate.minPkgDistance) < 0) {
      errors.minPkgDistance = 'Minimum Distance cannot be negative';
    }
    
    if (!rate.maxPkgDistance && rate.maxPkgDistance !== 0) {
      errors.maxPkgDistance = 'Maximum Distance is required';
    } else if (Number(rate.maxPkgDistance) < 0) {
      errors.maxPkgDistance = 'Maximum Distance cannot be negative';
    }
    
    if (!rate.pkgCharge && rate.pkgCharge !== 0) {
      errors.pkgCharge = 'Package Fee is required';
    } else if (Number(rate.pkgCharge) < 0) {
      errors.pkgCharge = 'Package Fee cannot be negative';
    }
    
    if (!rate.addnlPerKmCharge && rate.addnlPerKmCharge !== 0) {
      errors.addnlPerKmCharge = 'Per Km Charge is required';
    } else if (Number(rate.addnlPerKmCharge) < 0) {
      errors.addnlPerKmCharge = 'Per Km Charge cannot be negative';
    } else {
      const result = validatePerKmCharge(rate.addnlPerKmCharge, rewardConstraints);
      if (result !== true) errors.addnlPerKmCharge = result;
    }
    
    // Time validation only if isTimeBased is true
    if (rate.isTimeBased) {
      if (!rate.timeFrom) {
        errors.timeFrom = 'Time From is required';
      }
      if (!rate.timeTo) {
        errors.timeTo = 'Time To is required';
      }
      if (rate.timeFrom && rate.timeTo) {
        const timeFromSeconds = rate.timeFrom.getHours() * 3600 + rate.timeFrom.getMinutes() * 60 + rate.timeFrom.getSeconds();
        let timeToSeconds = rate.timeTo.getHours() * 3600 + rate.timeTo.getMinutes() * 60 + rate.timeTo.getSeconds();
        // Treat 12:00 AM (0 seconds) as end of day (86400 seconds) for Time To
        if (timeToSeconds === 0) {
          timeToSeconds = 86400;
        }
        if (timeToSeconds < timeFromSeconds) {
          errors.timeTo = 'End time should be greater than start time';
        }
      }
    }
    
    // Min/Max distance comparison
    if (Number(rate.minPkgDistance) >= 0 && Number(rate.maxPkgDistance) >= 0 && 
        rate.minPkgDistance !== '' && rate.maxPkgDistance !== '' &&
        Number(rate.minPkgDistance) >= Number(rate.maxPkgDistance)) {
      errors.maxPkgDistance = 'Maximum Distance should be greater than Minimum Distance';
    }
    
    return errors;
  };

  // Helper to get error for a specific field in a rate
  const getFieldError = (pkgIndex, rateIndex, fieldName) => {
    return validationErrors[pkgIndex]?.[rateIndex]?.[fieldName] || '';
  };

  // Helper to check if a package has any validation errors
  const hasPackageErrors = (pkgIndex) => {
    const pkgErrors = validationErrors[pkgIndex];
    if (!pkgErrors) return false;
    return Object.values(pkgErrors).some(rateErrors => 
      Object.keys(rateErrors).length > 0
    );
  };

  // Helper to check if the last rate in current package has validation errors
  const isLastRateInvalid = () => {
    if (currentRates.length === 0) return false;
    const lastRate = currentRates[currentRates.length - 1];
    const errors = validateRate(lastRate);
    return Object.keys(errors).length > 0;
  };

  const selectedPackage = packages[selectedPackageIndex];
  const currentRates = packageRates[selectedPackageIndex] || [];

  const toggleRowExpansion = (rateIndex) => {
    setExpandedRows((prev) => ({ ...prev, [rateIndex]: !prev[rateIndex] }));
  };

  const addRateSlab = () => {
    const newIndex = currentRates.length;
    setPackageRates((prev) => ({
      ...prev,
      [selectedPackageIndex]: [...(prev[selectedPackageIndex] || []), getEmptyRate()],
    }));
    setExpandedRows((prev) => ({ ...prev, [newIndex]: true }));
  };

  const removeRateSlab = (rateIndex) => {
    setPackageRates((prev) => {
      const rates = [...(prev[selectedPackageIndex] || [])];
      rates.splice(rateIndex, 1);
      return { ...prev, [selectedPackageIndex]: rates };
    });
    // Also remove validation errors for this rate
    setValidationErrors((prev) => {
      const pkgErrors = { ...prev[selectedPackageIndex] };
      delete pkgErrors[rateIndex];
      return { ...prev, [selectedPackageIndex]: pkgErrors };
    });
  };

  const handleRateChange = (rateIndex, field, value) => {
    setPackageRates((prev) => {
      const rates = [...(prev[selectedPackageIndex] || [])];
      rates[rateIndex] = { ...rates[rateIndex], [field]: value };
      
      // Validate only the specific field that changed
      const rate = rates[rateIndex];
      let fieldError = '';
      
      if (field === 'minPkgDistance') {
        if (!value && value !== 0) {
          fieldError = 'Minimum Distance is required';
        } else if (Number(value) < 0) {
          fieldError = 'Minimum Distance cannot be negative';
        }
      } else if (field === 'maxPkgDistance') {
        if (!value && value !== 0) {
          fieldError = 'Maximum Distance is required';
        } else if (Number(value) < 0) {
          fieldError = 'Maximum Distance cannot be negative';
        } else if (Number(rate.minPkgDistance) >= 0 && rate.minPkgDistance !== '' && Number(value) > 0 && Number(rate.minPkgDistance) >= Number(value)) {
          fieldError = 'Maximum Distance should be greater than Minimum Distance';
        }
      } else if (field === 'pkgCharge') {
        if (!value && value !== 0) {
          fieldError = 'Package Fee is required';
        } else if (Number(value) < 0) {
          fieldError = 'Package Fee cannot be negative';
        }
      } else if (field === 'addnlPerKmCharge') {
        if (!value && value !== 0) {
          fieldError = 'Per Km Charge is required';
        } else if (Number(value) < 0) {
          fieldError = 'Per Km Charge cannot be negative';
        } else {
          const result = validatePerKmCharge(value, rewardConstraints);
          if (result !== true) fieldError = result;
        }
      } else if (field === 'isTimeBased') {
        // When toggling time-based, validate time fields if checked
        if (value === true) {
          // Will be validated by isLastRateInvalid() using validateRate()
          // Clear any existing time errors since they'll be re-evaluated
        }
      } else if (field === 'timeFrom') {
        if (rate.isTimeBased && !value) {
          fieldError = 'Time From is required';
        }
      } else if (field === 'timeTo') {
        if (rate.isTimeBased) {
          if (!value) {
            fieldError = 'Time To is required';
          } else if (rate.timeFrom) {
            const timeFromSeconds = rate.timeFrom.getHours() * 3600 + rate.timeFrom.getMinutes() * 60;
            let timeToSeconds = value.getHours() * 3600 + value.getMinutes() * 60;
            if (timeToSeconds === 0) timeToSeconds = 86400;
            if (timeToSeconds <= timeFromSeconds) {
              fieldError = 'End time should be greater than start time';
            }
          }
        }
      }
      
      // Update only the specific field error
      setValidationErrors(prevErrors => ({
        ...prevErrors,
        [selectedPackageIndex]: {
          ...prevErrors[selectedPackageIndex],
          [rateIndex]: {
            ...(prevErrors[selectedPackageIndex]?.[rateIndex] || {}),
            [field]: fieldError
          }
        }
      }));
      
      return { ...prev, [selectedPackageIndex]: rates };
    });
  };

  const handleNextPackage = () => {
    // Validate current package rates before marking complete
    const rates = packageRates[selectedPackageIndex] || [];
    let hasErrors = false;
    const pkgErrors = {};
    
    rates.forEach((rate, index) => {
      const rateErrors = validateRate(rate);
      pkgErrors[index] = rateErrors;
      if (Object.keys(rateErrors).length > 0) {
        hasErrors = true;
      }
    });
    
    setValidationErrors(prev => ({
      ...prev,
      [selectedPackageIndex]: pkgErrors
    }));
    
    if (hasErrors) {
      showAlert("error", "Please fix validation errors before proceeding");
      return;
    }
    
    if (selectedPackageIndex < packages.length - 1) {
      setSelectedPackageIndex(selectedPackageIndex + 1);
      setExpandedRows({});
    }
  };

  const handleSaveAll = async () => {
    setHasAttemptedSave(true);
    // Validate all rates before saving
    let allValid = true;
    const newErrors = {};
    let firstErrorIndex = -1;
    
    packages.forEach((pkg, pkgIndex) => {
      newErrors[pkgIndex] = {};
      const rates = packageRates[pkgIndex] || [];
      rates.forEach((rate, rateIndex) => {
        const rateErrors = validateRate(rate);
        newErrors[pkgIndex][rateIndex] = rateErrors;
        if (Object.keys(rateErrors).length > 0) {
          allValid = false;
          if (firstErrorIndex === -1) {
            firstErrorIndex = pkgIndex;
          }
        }
      });
    });
    
    setValidationErrors(newErrors);
    
    if (!allValid) {
      if (firstErrorIndex !== -1) {
        setSelectedPackageIndex(firstErrorIndex);
        setExpandedRows({});
      }
      showAlert("error", "Validation errors found. Please fix all errors before saving.");
      return;
    }

    // Build packagesDTO: spread existing package data + add rates
    const packagesDTO = packages.map((pkg, i) => {
      const rates = packageRates[i] || [];

      return {
        ...pkg,
        id: null,
        // Add nested rates with all fields from existing form
        packageRates: rates.map((rate) => ({
          minPkgDistance: Number(rate.minPkgDistance) || 0,
          maxPkgDistance: Number(rate.maxPkgDistance) || 0,
          pkgDistance: Number(rate.maxPkgDistance) || 0,
          pkgCharge: Number(rate.pkgCharge) || 0,
          packageValue: Number(rate.pkgCharge) || 0,
          addnlPerKmCharge: Number(rate.addnlPerKmCharge) || 0,
          addnlTimeCharge: 0,
          addnlTimeChargeBlockInMin: 0,
          pkgTimeInMin: 0,
          isTaxInclusive: false,
          isTimeBased: rate.isTimeBased || false,
          isNightfareTimeSlot: false,
          packageRideType: pkg.serviceType || "All",
          timeFrom: rate.isTimeBased && rate.timeFrom
            ? Number(moment.duration(moment(rate.timeFrom).format("HH:mm:ss")).asSeconds())
            : 0,
          timeTo: (() => {
            if (!rate.isTimeBased || !rate.timeTo) return 86400;
            const seconds = Number(moment.duration(moment(rate.timeTo).format("HH:mm:ss")).asSeconds());
            // If timeTo is 12:00 AM (0 seconds), treat it as end of day (86400)
            return seconds === 0 ? 86400 : seconds;
          })(),
        })),
      };
    });

    console.log("packagesDTO:", packagesDTO);

    // If onSubmit callback is provided, use it instead of calling API directly
    if (onSubmit) {
      setIsSaving(true);
      try {
        const result = await onSubmit(packagesDTO);
        if (result !== false && onComplete) onComplete();
      } catch (error) {
        console.error("Error submitting packages:", error);
        // Error handling is done by parent
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Default behavior: call API directly
    setIsSaving(true);
    try {
      // Call bulk API
      const response = await createPackagesBulk(packagesDTO);
      if (response?.status === 201) {
        showAlert("success", `Created ${packagesDTO.length} package(s) with rates successfully!`);
        if (onComplete) onComplete();
      }
    } catch (error) {
      console.error("Error creating packages:", error);
      showAlert("error", error.response?.data?.message || "Failed to create packages");;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "70vh" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Create Package Rates
        </Typography>
        <Box>
          <Button
            variant="outlined"
            onClick={onBack}
            disabled={isSaving}
            sx={{ mr: 1, borderColor: "#666", color: "#666" }}
          >
            ← Back
          </Button>
          
          {selectedPackageIndex < packages.length - 1 ? (
             <Button
               variant="contained"
               onClick={handleNextPackage}
               sx={{ backgroundColor: "#007bff", "&:hover": { backgroundColor: "#0056b3" } }}
             >
                Next Package →
             </Button>
          ) : (
             <Button
               variant="contained"
               onClick={handleSaveAll}
               disabled={isSaving || !packages.every((_, idx) => packageRates[idx]?.length > 0)}
               sx={{
                  backgroundColor: !packages.every((_, idx) => packageRates[idx]?.length > 0) ? "#ccc" : "#39B54A",
                  "&:hover": { backgroundColor: !packages.every((_, idx) => packageRates[idx]?.length > 0) ? "#ccc" : "#2d8f3a" } 
               }}
             >
               Done
             </Button>
          )}

        </Box>
      </Box>

      {/* Main Content */}
      <Paper sx={{ flexGrow: 1, display: "flex", overflow: "hidden", border: "1px solid #e0e0e0" }}>
        {/* Sidebar */}
        <Box sx={{ width: "20%", flexShrink: 0, borderRight: "1px solid #e0e0e0", display: "flex", flexDirection: "column", bgcolor: "#f5f5f5" }}>
          <Typography variant="subtitle1" sx={{ p: 2, fontWeight: "bold", borderBottom: "1px solid #ccc", bgcolor: "rgba(225, 244, 228, 1)" }}>
            Packages ({packages.length})
          </Typography>
          <Box sx={{ overflowY: "auto", flexGrow: 1, '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
            <List disablePadding>
              {packages.map((pkg, idx) => (
                <ListItem
                  button
                  key={idx}
                  selected={selectedPackageIndex === idx}
                  onClick={() => { setSelectedPackageIndex(idx); setExpandedRows({}); }}
                  sx={{ "&.Mui-selected": { borderRight: "4px solid #39B54A" }, borderBottom: "1px solid #ddd", bgcolor: "#fff !important", px: 2, py: 1.5 }}
                >
                  <ListItemText
                    primary={pkg.name}
                    secondary={`${pkg.serviceCityName} - ${pkg.carTypeName}`}
                    primaryTypographyProps={{ fontSize: "14px", fontWeight: 500 }}
                    secondaryTypographyProps={{ fontSize: "12px" }}
                  />
                  {hasAttemptedSave && hasPackageErrors(idx) && <ErrorIcon sx={{ color: "red", fontSize: "20px", mr: 1 }} />}
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>

        {/* Main Edit Area */}
        <Box sx={{ flexGrow: 1, overflowY: "auto", p: 3, '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
          {selectedPackage ? (
            <>
              <Typography variant="h6" sx={{ mb: 2, color: "#39B54A" }}>
                {selectedPackage.name} – Rates
              </Typography>

              {currentRates.length > 0 ? (
                <Table sx={{ border: '1px solid rgba(217, 217, 217, 1)', borderRadius: '8px' }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'rgba(225, 244, 228, 1)' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '50px' }}></TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Min Distance</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Max Distance</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Package Fee</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Per Km Charge</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Time Based</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Time From</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Time To</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '50px' }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentRates.map((rate, index) => (
                      <React.Fragment key={index}>
                        <TableRow
                          onClick={() => toggleRowExpansion(index)}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f9f9f9' }, bgcolor: expandedRows[index] ? 'rgba(225, 244, 228, 0.5)' : 'inherit' }}
                        >
                          <TableCell>
                            <IconButton size="small">
                              {expandedRows[index] ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ color: getFieldError(selectedPackageIndex, index, 'minPkgDistance') ? 'red' : 'inherit' }}>{rate.minPkgDistance || '-'}</TableCell>
                          <TableCell sx={{ color: getFieldError(selectedPackageIndex, index, 'maxPkgDistance') ? 'red' : 'inherit' }}>{rate.maxPkgDistance || '-'}</TableCell>
                          <TableCell sx={{ color: getFieldError(selectedPackageIndex, index, 'pkgCharge') ? 'red' : 'inherit' }}>{rate.pkgCharge || '-'}</TableCell>
                          <TableCell sx={{ color: getFieldError(selectedPackageIndex, index, 'addnlPerKmCharge') ? 'red' : 'inherit' }}>{rate.addnlPerKmCharge || '-'}</TableCell>
                          <TableCell>{rate.isTimeBased ? 'Yes' : 'No'}</TableCell>
                          <TableCell>{rate.isTimeBased && rate.timeFrom ? moment(rate.timeFrom).format('h:mm A') : '-'}</TableCell>
                          <TableCell sx={{ color: getFieldError(selectedPackageIndex, index, 'timeTo') ? 'red' : 'inherit' }}>{rate.isTimeBased && rate.timeTo ? moment(rate.timeTo).format('h:mm A') : '-'}</TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeRateSlab(index); }} sx={{ color: 'red' }}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={9} sx={{ py: 0, borderBottom: expandedRows[index] ? '1px solid rgba(217, 217, 217, 1)' : 'none' }}>
                            <Collapse in={expandedRows[index]} timeout={300}>
                              <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField 
                                      fullWidth 
                                      label="Min Distance (km)" 
                                      type="number" 
                                      value={rate.minPkgDistance} 
                                      onChange={(e) => e.target.value.length <= 6 && handleRateChange(index, "minPkgDistance", e.target.value)} 
                                      onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                                      size="small" 
                                      error={!!getFieldError(selectedPackageIndex, index, 'minPkgDistance')}
                                      helperText={getFieldError(selectedPackageIndex, index, 'minPkgDistance')}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField 
                                      fullWidth 
                                      label="Max Distance (km)" 
                                      type="number" 
                                      value={rate.maxPkgDistance} 
                                      onChange={(e) => e.target.value.length <= 6 && handleRateChange(index, "maxPkgDistance", e.target.value)} 
                                      onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                                      size="small" 
                                      error={!!getFieldError(selectedPackageIndex, index, 'maxPkgDistance')}
                                      helperText={getFieldError(selectedPackageIndex, index, 'maxPkgDistance')}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField 
                                      fullWidth 
                                      label="Package Fee" 
                                      type="number" 
                                      value={rate.pkgCharge} 
                                      onChange={(e) => e.target.value.length <= 7 && handleRateChange(index, "pkgCharge", e.target.value)} 
                                      onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                                      size="small" 
                                      error={!!getFieldError(selectedPackageIndex, index, 'pkgCharge')}
                                      helperText={getFieldError(selectedPackageIndex, index, 'pkgCharge')}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={6} md={3}>
                                    <TextField 
                                      fullWidth 
                                      label="Per Km Charge" 
                                      type="number" 
                                      value={rate.addnlPerKmCharge} 
                                      onChange={(e) => e.target.value.length <= 6 && handleRateChange(index, "addnlPerKmCharge", e.target.value)} 
                                      onKeyDown={(e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                                      size="small" 
                                      error={!!getFieldError(selectedPackageIndex, index, 'addnlPerKmCharge')}
                                      helperText={getFieldError(selectedPackageIndex, index, 'addnlPerKmCharge')}
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <FormControlLabel
                                      control={<Checkbox checked={rate.isTimeBased} onChange={(e) => handleRateChange(index, "isTimeBased", e.target.checked)} sx={{ color: "#39B54A", "&.Mui-checked": { color: "#39B54A" } }} />}
                                      label="Time Based"
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Collapse in={rate.isTimeBased} timeout={300}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                          <div style={{ marginBottom: '5px', fontSize: '12px', color: getFieldError(selectedPackageIndex, index, 'timeFrom') ? 'red' : 'rgba(0, 0, 0, 0.6)' }}>Time From</div>
                                          <DatePicker className="form-control" selected={rate.timeFrom} onChange={(date) => handleRateChange(index, "timeFrom", date)} showTimeSelect showTimeSelectOnly timeIntervals={60} dateFormat="h:mm aa" placeholderText="Select Time From" />
                                          {getFieldError(selectedPackageIndex, index, 'timeFrom') && (
                                            <Typography variant="caption" color="error">{getFieldError(selectedPackageIndex, index, 'timeFrom')}</Typography>
                                          )}
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                          <div style={{ marginBottom: '5px', fontSize: '12px', color: getFieldError(selectedPackageIndex, index, 'timeTo') ? 'red' : 'rgba(0, 0, 0, 0.6)' }}>Time To</div>
                                          <DatePicker className="form-control" selected={rate.timeTo} onChange={(date) => handleRateChange(index, "timeTo", date)} showTimeSelect showTimeSelectOnly timeIntervals={60} dateFormat="h:mm aa" placeholderText="Select Time To" />
                                          {getFieldError(selectedPackageIndex, index, 'timeTo') && (
                                            <Typography variant="caption" color="error">{getFieldError(selectedPackageIndex, index, 'timeTo')}</Typography>
                                          )}
                                        </Grid>
                                      </Grid>
                                    </Collapse>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                  No rates added yet. Click "Add Rate Slab" to create one.
                </Typography>
              )}

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={addRateSlab}
                  disabled={isLastRateInvalid()}
                  sx={{ 
                    borderColor: isLastRateInvalid() ? "#ccc" : "#39B54A", 
                    color: isLastRateInvalid() ? "#999" : "#39B54A", 
                    "&:hover": { borderColor: isLastRateInvalid() ? "#ccc" : "#2d8f3a", bgcolor: isLastRateInvalid() ? "transparent" : "rgba(57,181,74,0.1)" },
                    "&.Mui-disabled": { borderColor: "#ccc", color: "#999" }
                  }}
                >
                  Add Rate Slab
                </Button>
                
                {/* Copy Rates From dropdown - only show if other packages have rates */}
                {packages.some((_, idx) => idx !== selectedPackageIndex && (packageRates[idx]?.length || 0) > 0) && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<ContentCopy />}
                      onClick={(e) => setCopyMenuAnchor(e.currentTarget)}
                      sx={{ borderColor: "#007bff", color: "#007bff", "&:hover": { borderColor: "#0056b3", bgcolor: "rgba(0,123,255,0.1)" } }}
                    >
                      Copy Rates From
                    </Button>
                    <Menu
                      anchorEl={copyMenuAnchor}
                      open={Boolean(copyMenuAnchor)}
                      onClose={() => setCopyMenuAnchor(null)}
                    >
                      {packages.map((pkg, idx) => {
                        const rateCount = packageRates[idx]?.length || 0;
                        // Skip current package and packages with no rates
                        if (idx === selectedPackageIndex || rateCount === 0) return null;
                        
                        // Check if source package has validation errors
                        const sourceRates = packageRates[idx] || [];
                        const hasSourceErrors = sourceRates.some(rate => {
                          const errors = validateRate(rate);
                          return Object.keys(errors).length > 0;
                        });
                        
                        return (
                          <MenuItem
                            key={idx}
                            onClick={() => {
                              // Validate source package before copying
                              if (hasSourceErrors) {
                                showAlert("warning", `Cannot copy from "${pkg.name}" - it has validation errors. Please fix them first.`);
                                setCopyMenuAnchor(null);
                                return;
                              }
                              
                              // Deep clone rates from source package
                              const clonedRates = sourceRates.map(rate => ({
                                ...rate,
                                timeFrom: rate.timeFrom ? new Date(rate.timeFrom) : null,
                                timeTo: rate.timeTo ? new Date(rate.timeTo) : null,
                              }));
                              // Append to current package rates
                              setPackageRates(prev => ({
                                ...prev,
                                [selectedPackageIndex]: [...(prev[selectedPackageIndex] || []), ...clonedRates],
                              }));
                              setCopyMenuAnchor(null);
                              showAlert("success", `Copied ${clonedRates.length} rate(s) from ${pkg.name}`);
                            }}
                            sx={{ color: hasSourceErrors ? '#999' : 'inherit' }}
                          >
                            {pkg.name} ({rateCount} rate{rateCount !== 1 ? 's' : ''})
                            {hasSourceErrors && ' ⚠️'}
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </>
                )}
              </Box>


            </>
          ) : (
            <Typography sx={{ textAlign: "center", mt: 5, color: "#888" }}>
              Select a package from the list to add rates.
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default PackageRateCreateForm;
